// apps/web/src/features/clients/components/ClientSummary.tsx
//
// The answer to "who is this and who do I call" without scrolling: manager,
// primary contact with its call/copy actions, and the free-text comment. It sits
// above the two equal sections so neither Contacts nor Legal entities has to
// carry the client's own identity (AC-5).
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientDetail } from '@magimp/shared';
import { Chip, ContactActions, SectionCard } from '../../../ui/index.js';
import { useCoarsePointer } from '../useMobileFieldStyle.js';
import { CopyTextButton } from './CopyTextButton.js';
import { displayPhone, TelLink } from './clientRowParts.js';

export interface ClientSummaryProps {
  client: ClientDetail;
}

export function ClientSummary({ client }: ClientSummaryProps) {
  const { t } = useTranslation(['clients', 'common']);
  const touch = useCoarsePointer();
  const primary = client.contacts.find((c) => c.isPrimary && !c.archivedAt) ?? null;
  const callLabel = primary
    ? t('clients:actions.callContact', { name: primary.fullName })
    : undefined;

  return (
    <SectionCard title={t('clients:sections.summary')}>
      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, auto) minmax(0, 1fr)',
          gap: 'var(--s-2) var(--s-4)',
          margin: 0,
          minWidth: 0,
        }}
      >
        <Term>{t('clients:fields.manager')}</Term>
        <Detail>{client.manager.name}</Detail>

        <Term>{t('clients:fields.primaryContact')}</Term>
        <Detail>
          {primary === null ? (
            <span style={{ color: 'var(--fg-4)' }}>{t('clients:noPrimaryContact')}</span>
          ) : (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--s-3)',
                flexWrap: 'wrap',
                minWidth: 0,
              }}
            >
              <span style={{ overflowWrap: 'anywhere' }}>{primary.fullName}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--fg-2)' }}>
                {displayPhone(primary.phone)}
              </span>
              {touch ? (
                <TelLink href={`tel:${primary.phone}`} label={callLabel ?? ''} />
              ) : (
                <ContactActions
                  actions={[
                    { icon: 'phone', href: `tel:${primary.phone}`, label: callLabel ?? '' },
                  ]}
                />
              )}
              <CopyTextButton
                text={primary.phone}
                label={t('clients:actions.copyPhoneOf', { name: primary.fullName })}
                testId="client-summary-copy-phone"
              />
            </span>
          )}
        </Detail>

        {client.lifecycle && (
          <>
            <Term>{t('clients:fields.lifecycle')}</Term>
            <Detail>
              <Chip>
                {client.lifecycle === 'WORKING'
                  ? t('clients:lifecycle.working')
                  : t('clients:lifecycle.potential')}
              </Chip>
            </Detail>
          </>
        )}

        {client.comment && (
          <>
            <Term>{t('clients:fields.comment')}</Term>
            <Detail>
              <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {client.comment}
              </span>
            </Detail>
          </>
        )}
      </dl>
    </SectionCard>
  );
}

function Term({ children }: { children: string }) {
  return (
    <dt style={{ color: 'var(--fg-3)', fontSize: 'var(--fz-12)', whiteSpace: 'nowrap' }}>
      {children}
    </dt>
  );
}

function Detail({ children }: { children: ReactNode }) {
  return <dd style={{ margin: 0, minWidth: 0 }}>{children}</dd>;
}
